/*
 * Phone-number formatting + validation for the organization registration form, backed by
 * libphonenumber-js (pinned). The Business phone field auto-formats AS THE OPERATOR TYPES, using the
 * grouping of the country selected in the form (country of registration), so a US org gets
 * "(555) 123-4567", a UK org "020 7946 0958", etc. The stored value is normalised to E.164
 * (`+15551234567`) when the number is valid, so the backing data is canonical regardless of the
 * display grouping.
 *
 * Country handling: the form's country value is an ISO 3166-1 alpha-2 code (from geo.ts), which is
 * exactly what libphonenumber calls a `CountryCode`. When no country is selected the formatter still
 * works for an international number the operator types with a leading `+`.
 */

import {
  AsYouType,
  getExampleNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";

/** A country value from the form (`""` when none selected) narrowed to a libphonenumber CountryCode. */
function toCountry(country: string | null | undefined): CountryCode | undefined {
  if (country === null || country === undefined || country === "") return undefined;
  // libphonenumber accepts any ISO alpha-2; a value it does not know simply yields no national rules.
  return country.toUpperCase() as CountryCode;
}

/**
 * Format `input` as the operator types, using the selected country's national grouping. Pure and
 * stable: it never rejects keystrokes (so backspace/edits work), it just regroups what is there.
 * With no country and no leading `+`, it returns the input digits lightly cleaned.
 */
export function formatPhoneAsYouType(input: string, country: string | null | undefined): string {
  if (input.trim() === "") return "";
  const cc = toCountry(country);
  const formatter = cc ? new AsYouType(cc) : new AsYouType();
  return formatter.input(input);
}

/**
 * Format a phone number for DISPLAY in the organization form. The product wants the country-code
 * prefix WITH the country's national grouping, e.g. a US/CA number shows as `+1 (647) 293-3484`
 * (libphonenumber's own INTERNATIONAL format uses spaces, `+1 647 293 3484`, which the design does
 * not want). So once the number PARSES as a complete number we compose `+<callingCode> <national>`
 * — the national part carries the country's own grouping (parentheses/dashes). While the number is
 * still PARTIAL (being typed) we fall back to the as-you-type national grouping so editing stays
 * smooth. An empty value stays empty.
 */
export function formatPhoneForDisplay(input: string, country: string | null | undefined): string {
  if (input.trim() === "") return "";
  const cc = toCountry(country);
  const parsed = parsePhoneNumberFromString(input, cc);
  if (parsed && parsed.isValid()) {
    // `+<callingCode> <national-with-grouping>` — e.g. "+1 (647) 293-3484".
    return `+${parsed.countryCallingCode} ${parsed.formatNational()}`;
  }
  // Partial / not-yet-valid: keep the smooth as-you-type national grouping.
  const formatter = cc ? new AsYouType(cc) : new AsYouType();
  return formatter.input(input);
}

/**
 * Normalise a phone number to E.164 (`+15551234567`) for storage when it is a VALID number for the
 * selected country (or a valid international number with a leading `+`). Returns the trimmed input
 * unchanged when it is not (yet) valid, so a partial number is never silently mangled on save.
 */
export function normalizePhoneForStorage(input: string, country: string | null | undefined): string {
  const v = input.trim();
  if (v === "") return "";
  const parsed = parsePhoneNumberFromString(v, toCountry(country));
  return parsed && parsed.isValid() ? parsed.number : v;
}

/**
 * Validate a phone number for the selected country. Empty is allowed (the field is optional). A
 * non-empty value must be a POSSIBLE/valid number for the country (or a valid international number).
 * Returns an error string or null (matching the convention in `validate.ts`).
 */
export function validatePhoneForCountry(
  input: string,
  country: string | null | undefined,
  label = "Phone",
): string | null {
  const v = input.trim();
  if (v === "") return null;
  const parsed = parsePhoneNumberFromString(v, toCountry(country));
  if (parsed && parsed.isValid()) return null;
  return country && country !== ""
    ? `${label} is not a valid number for the selected country.`
    : `${label} is not a valid phone number (include a country code, e.g. +1 …).`;
}

/**
 * An example phone number for the selected country, formatted nationally, for use as the input
 * PLACEHOLDER so the operator sees the expected shape. Falls back to a generic international hint
 * when no country is selected or no example exists.
 */
export function examplePhonePlaceholder(country: string | null | undefined): string {
  const cc = toCountry(country);
  if (cc === undefined) return "+1 555 123 4567";
  try {
    const ex = getExampleNumber(cc, examples);
    if (ex) return ex.formatNational();
  } catch {
    /* fall through to the generic hint */
  }
  return "Enter a phone number";
}
